/**
 * Policies list — saved layout storage shape (OR-1664).
 *
 * Same behaviour as Submission Triage's "remember my last layout"
 * (OR-1384): the columns the user shows, their order and the filters they
 * left applied come back after a refresh. Stored in the backend's opaque
 * per-user blob under `VIEW_KEYS.policies` (backend OR-1385) — one object
 * per user, last write wins, 32 KB cap.
 *
 * No named Saved Views here: the Policies design has no such control.
 *
 * **Compatibility.** The blob stores column ids and `policy-columns.ts`
 * changes with deploys, so ids are always intersected with the current
 * column set on read: a stale layout degrades, it never blanks the table.
 * Filters are rebuilt field by field, dropping anything the backend's
 * enums no longer accept, because `filtersToParams` forwards them
 * verbatim.
 */
import type { ViewPreferenceConfig } from "../../api";
import { POLICY_PRIORITIES, POLICY_TERM_STATUSES } from "../../api";
import type { PolicyPriority, PolicyTermStatus } from "../../api";
import { EMPTY_FILTERS, EXPIRATION_PRESETS, type ExpirationPreset, type FilterOption, type PolicyFilters } from "./filters";
import { allColumnIds, defaultVisibleIds } from "./policy-columns";

/** Bump on any incompatible change to the stored shape. */
export const CONFIG_VERSION = 1;

/**
 * The layout the user last left the page in. Saved automatically on every
 * commit (Customize save, filter apply, chip removal), with no UI of its
 * own. Excludes the search box and the sort directive: a search is a
 * one-off lookup, not a preference.
 */
export interface PolicyLayout {
    visibleColumnIds: string[];
    columnOrder: string[];
    filters: PolicyFilters;
}

// ───────────────────────────── serialize ─────────────────────────────

function sameIds(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, i) => id === b[i]);
}

/** Column arrays are omitted when they match the defaults (the common
 *  case); the parser treats a missing array as "use the defaults". */
export function serializeLayout(layout: PolicyLayout): ViewPreferenceConfig {
    const entry: Record<string, unknown> = { filters: layout.filters };
    if (!sameIds(layout.visibleColumnIds, defaultVisibleIds())) entry.visible_column_ids = layout.visibleColumnIds;
    if (!sameIds(layout.columnOrder, allColumnIds())) entry.column_order = layout.columnOrder;
    return { v: CONFIG_VERSION, last_layout: entry };
}

// ──────────────────────────── deserialize ────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asStringArray(value: unknown): string[] | null {
    if (!Array.isArray(value)) return null;
    return value.every((v) => typeof v === "string") ? (value as string[]) : null;
}

function asString(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function asFilterOption(value: unknown): FilterOption | null {
    if (!isRecord(value)) return null;
    const { id, label } = value;
    if (typeof id !== "string" || typeof label !== "string") return null;
    return { id, label };
}

/** Keep only the values an enum still accepts, in the saved order. */
function onlyKnown<T extends string>(value: unknown, known: readonly T[]): T[] {
    const saved = asStringArray(value) ?? [];
    const set = new Set<string>(known);
    return saved.filter((v): v is T => set.has(v));
}

/** Saved sequence for columns that still exist, then any column the blob
 *  never knew about. Never returns an id that isn't a real column. */
function reconcileOrder(saved: string[] | null): string[] {
    const natural = allColumnIds();
    if (!saved) return natural;
    const known = new Set(natural);
    const kept = saved.filter((id) => known.has(id));
    const seen = new Set(kept);
    return [...kept, ...natural.filter((id) => !seen.has(id))];
}

/** Visible ids, dropping columns that no longer exist. Falls back to the
 *  defaults when the saved set is missing or ends up empty — a table with
 *  zero columns is broken, not a preference. */
function reconcileVisible(saved: string[] | null): string[] {
    if (!saved) return defaultVisibleIds();
    const known = new Set(allColumnIds());
    const kept = saved.filter((id) => known.has(id));
    return kept.length > 0 ? kept : defaultVisibleIds();
}

function reconcileFilters(saved: unknown): PolicyFilters {
    if (!isRecord(saved)) return EMPTY_FILTERS;
    const preset = asString(saved.expirationPreset);
    return {
        priorities: onlyKnown<PolicyPriority>(saved.priorities, POLICY_PRIORITIES),
        statuses: onlyKnown<PolicyTermStatus>(saved.statuses, POLICY_TERM_STATUSES),
        inceptionStart: asString(saved.inceptionStart),
        inceptionEnd: asString(saved.inceptionEnd),
        expirationPreset: (EXPIRATION_PRESETS as readonly string[]).includes(preset) ? (preset as ExpirationPreset) : "",
        expirationStart: asString(saved.expirationStart),
        expirationEnd: asString(saved.expirationEnd),
        brokerage: asFilterOption(saved.brokerage),
    };
}

/**
 * The stored layout, or null when nothing was saved or the blob is
 * unreadable (unknown version, the `{}` an unsaved slot returns).
 */
export function parseLayout(config: unknown): PolicyLayout | null {
    if (!isRecord(config) || config.v !== CONFIG_VERSION) return null;
    const raw = config.last_layout;
    if (!isRecord(raw)) return null;
    return {
        visibleColumnIds: reconcileVisible(asStringArray(raw.visible_column_ids)),
        columnOrder: reconcileOrder(asStringArray(raw.column_order)),
        filters: reconcileFilters(raw.filters),
    };
}
